import { Page, PageHeader, Row, Rows, Section } from '@/components/page'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { BusinessMenuButton, BusinessSidebar } from '@/components/layout/BusinessSidebar'
import type { BusinessAddress } from '@/types'
import { MapPin, Mail, Globe, Tag, Building2, Users, Landmark, Link2 } from 'lucide-react'

type BusinessDetails = {
  id: string
  name: string
}

type Props = {
  business: BusinessDetails
  formation: FormationDetails | null
}

type FormationDetails = {
  id: string
  legal_name: string
  stage: string
  entity_name: string
  jurisdiction: string
  founder_location: string | null
  business_description: string | null
  online_presence: {
    kind: 'website' | 'social' | 'app' | 'prelaunch'
    url: string | null
  } | null
  primary_contact: { email: string; phone: string | null } | null
  business_address_provider: string | null
  business_address: Omit<BusinessAddress, 'id' | 'label'> | null
  planned_ownership_label: string
  planned_ownership: { name: string; interest: string }[]
  roles: { managers: string[]; directors: string[]; president: string | null; secretary: string | null }
  planned_capital: {
    authorized_common_shares: number
    employee_pool_basis_points: number
    par_value_policy: string
    par_value: string | null
  } | null
}

const ONLINE_PRESENCE_LABELS: Record<NonNullable<FormationDetails['online_presence']>['kind'], string> = {
  website: 'Website',
  social: 'Social profile',
  app: 'App',
  prelaunch: 'Not online yet — pre-launch',
}

function addressLine(address: FormationDetails['business_address']) {
  if (!address) return null
  return [
    address.street_address_1,
    address.street_address_2,
    [address.city, address.state_province].filter(Boolean).join(', '),
    [address.postal_code, address.country_code].filter(Boolean).join(' · '),
  ].filter(Boolean).join(', ')
}

export default function BusinessDetails({ business, formation }: Props) {
  return (
    <DashboardLayout
      title="Details"
      breadcrumbs={[
        { label: 'Businesses', href: 'businesses' },
        { label: business.name, href: `businesses/${business.id}` },
        { label: 'Details' },
      ]}
      guide={<BusinessSidebar businessId={business.id} />}
      mobileGuide={<BusinessMenuButton businessId={business.id} />}
    >
      <Page>
        <PageHeader title={<>Details</>} subtitle={<>{business.name}</>} />

        {formation && (
          <>
            <Section title="Formation details">
              <Rows>
                <Row
                  icon={<Building2 />}
                  title={formation.legal_name}
                  description={`${formation.entity_name} · ${formation.jurisdiction}`}
                />
                {formation.founder_location && (
                  <Row icon={<Globe />} title="Founder location" description={formation.founder_location} />
                )}
                {formation.business_description && (
                  <Row icon={<Tag />} title="Business description" description={formation.business_description} />
                )}
                {formation.online_presence && (
                  <Row
                    icon={<Link2 />}
                    title="Online presence"
                    description={
                      formation.online_presence.url ? (
                        <a
                          href={formation.online_presence.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="break-all underline underline-offset-4 transition-colors hover:text-foreground"
                        >
                          {ONLINE_PRESENCE_LABELS[formation.online_presence.kind]} · {formation.online_presence.url}
                        </a>
                      ) : ONLINE_PRESENCE_LABELS[formation.online_presence.kind]
                    }
                  />
                )}
                {formation.primary_contact && (
                  <Row
                    icon={<Mail />}
                    title={formation.primary_contact.email}
                    description={formation.primary_contact.phone || undefined}
                  />
                )}
                <Row
                  icon={<MapPin />}
                  title={formation.business_address_provider === 'corppy' ? 'Corppy business address' : 'Business address'}
                  description={addressLine(formation.business_address) || 'Provided by Corppy as part of the formation.'}
                />
              </Rows>
            </Section>

            {formation.planned_ownership.length > 0 && (
              <Section title={formation.planned_ownership_label}>
                <Rows>
                  {formation.planned_ownership.map((owner) => (
                    <Row key={`${owner.name}-${owner.interest}`} icon={<Users />} title={owner.name} description={owner.interest} />
                  ))}
                </Rows>
              </Section>
            )}

            {(formation.roles.managers.length > 0 || formation.roles.directors.length > 0 || formation.roles.president || formation.roles.secretary) && (
              <Section title="Roles and officers">
                <Rows>
                  {formation.roles.managers.map((name) => <Row key={`manager-${name}`} icon={<Users />} title={name} description="Manager" />)}
                  {formation.roles.directors.map((name) => <Row key={`director-${name}`} icon={<Users />} title={name} description="Director" />)}
                  {formation.roles.president && <Row icon={<Users />} title={formation.roles.president} description="President" />}
                  {formation.roles.secretary && <Row icon={<Users />} title={formation.roles.secretary} description="Secretary" />}
                </Rows>
              </Section>
            )}

            {formation.planned_capital && (
              <Section title="Planned share capital">
                <Rows>
                  <Row
                    icon={<Landmark />}
                    title={`${formation.planned_capital.authorized_common_shares.toLocaleString()} authorized common shares`}
                    description={`${formation.planned_capital.employee_pool_basis_points / 100}% employee pool · ${
                      formation.planned_capital.par_value_policy === 'par_value'
                        ? `$${formation.planned_capital.par_value} par value per share`
                        : 'No par value'
                    }`}
                  />
                </Rows>
              </Section>
            )}
          </>
        )}

      </Page>
    </DashboardLayout>
  )
}
